import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Autocomplete, FormControlLabel, Switch, Box, Typography, CircularProgress } from "@mui/material";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { getClients } from "../redux/ducks/client";
import { getClientAccounts } from "../redux/ducks/client";
import { getAccountProjects } from "../redux/ducks/account";
import { getProject } from "../redux/ducks/project";
import { recordMeetingTime } from "../redux/ducks/calendar";
import { showAlert } from "../redux/slices/alerts";
import AppDatePicker from "./AppDatePicker";
import { toLocalDay } from "../utils/dateUtils";

const StyledContent = styled(DialogContent)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
}));

const Row = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    "& > *": { flex: 1, minWidth: 180 },
}));

const ErrorBox = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.error.main}`,
    backgroundColor: theme.palette.error.light + "22",
    padding: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
}));

const SubmitSpinner = styled(CircularProgress)({ marginLeft: 8 });

const computeDurationHours = (start, end) => {
    if (!start || !end) return 0.5;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (!Number.isFinite(ms) || ms <= 0) return 0.5;
    return Math.max(0.01, Math.round((ms / 3600000) * 100) / 100);
};

const PROJECT_NAME_PM = /Project Management/i;
const PROJECT_NAME_MISC = "External / Misc Meetings";

const normalizeForSearch = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const fuzzyFilter = (getLabel) => (options, { inputValue }) => {
    const q = normalizeForSearch(inputValue);
    if (!q) return options;
    return options.filter((o) => normalizeForSearch(getLabel(o)).includes(q));
};

const MeetingTimeEntryDialog = ({ open, onClose, meeting }) => {
    const dispatch = useDispatch();
    const wasOpenRef = useRef(false);
    const lastMeetingIdRef = useRef(null);
    const suggestedMatchRef = useRef(null);

    const [clientOptions, setClientOptions] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);

    const [accountOptions, setAccountOptions] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);

    const [projectOptions, setProjectOptions] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);

    const [taskOptions, setTaskOptions] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(false);

    const [client, setClient] = useState(null);
    const [account, setAccount] = useState(null);
    const [project, setProject] = useState(null);
    const [task, setTask] = useState(null);

    const [duration, setDuration] = useState(0.5);
    const [summary, setSummary] = useState("");
    const [date, setDate] = useState(toLocalDay());
    const [billable, setBillable] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [inlineError, setInlineError] = useState(null);

    const category = meeting?.category || "misc";

    useEffect(() => {
        if (!open || !meeting) return;
        if (wasOpenRef.current && lastMeetingIdRef.current === meeting._id) return;
        wasOpenRef.current = true;
        lastMeetingIdRef.current = meeting._id;
        suggestedMatchRef.current = meeting.suggestedMatch || null;

        setInlineError(null);
        setSubmitting(false);
        setSummary(meeting.title || "");
        setDuration(computeDurationHours(meeting.start, meeting.end));
        setDate(toLocalDay(meeting.start ? new Date(meeting.start) : new Date()) || toLocalDay());
        setBillable((meeting.category || "misc") === "client");
        setTask(null);
        setProject(null);
        setAccount(null);
        setClient(null);
        setProjectOptions([]);
        setAccountOptions([]);
        setTaskOptions([]);

        let cancelled = false;
        setClientsLoading(true);
        dispatch(
            getClients(0, 0, (data) => {
                if (cancelled) return;
                setClientsLoading(false);
                const list = Array.isArray(data) ? data : data?.clients || [];
                setClientOptions(list);

                const sm = suggestedMatchRef.current;
                let prefillClient = null;
                if (sm?.client) {
                    prefillClient = list.find((c) => c._id === (sm.client._id || sm.client)) || null;
                }
                const cat = meeting.category || "misc";
                if (!prefillClient && (cat === "internal" || cat === "misc")) {
                    prefillClient = list.find((c) => c.isInternal === true) || null;
                }
                if (prefillClient) {
                    setClient(prefillClient);
                }
            }),
        );
        return () => {
            cancelled = true;
        };
    }, [open, meeting, dispatch]);

    useEffect(() => {
        if (!open) {
            wasOpenRef.current = false;
            lastMeetingIdRef.current = null;
        }
    }, [open]);

    useEffect(() => {
        if (!client?._id) {
            setAccountOptions([]);
            return;
        }
        let cancelled = false;
        setAccountsLoading(true);
        dispatch(
            getClientAccounts(client._id, (data) => {
                if (cancelled) return;
                setAccountsLoading(false);
                const list = Array.isArray(data) ? data : data?.accounts || [];
                setAccountOptions(list);

                const sm = suggestedMatchRef.current;
                if (sm?.account) {
                    const matchId = sm.account._id || sm.account;
                    const found = list.find((a) => a._id === matchId);
                    if (found) {
                        setAccount(found);
                        return;
                    }
                }
            }),
        );
        return () => {
            cancelled = true;
        };
    }, [client?._id, dispatch]);

    useEffect(() => {
        if (!account?._id) {
            setProjectOptions([]);
            return;
        }
        let cancelled = false;
        setProjectsLoading(true);
        dispatch(
            getAccountProjects(account._id, "open", (data) => {
                if (cancelled) return;
                setProjectsLoading(false);
                const list = Array.isArray(data) ? data : data?.projects || [];
                setProjectOptions(list);

                const sm = suggestedMatchRef.current;
                let prefill = null;
                if (sm?.project) {
                    const matchId = sm.project._id || sm.project;
                    prefill = list.find((p) => p._id === matchId) || null;
                }
                if (!prefill && category === "internal") {
                    prefill = list.find((p) => p.name && PROJECT_NAME_PM.test(p.name)) || null;
                }
                if (!prefill && category === "misc") {
                    prefill = list.find((p) => p.name === PROJECT_NAME_MISC) || null;
                }
                if (prefill) {
                    setProject(prefill);
                }
            }),
        );
        return () => {
            cancelled = true;
        };
    }, [account?._id, dispatch, category]);

    useEffect(() => {
        if (!project?._id) {
            setTaskOptions([]);
            return;
        }
        let cancelled = false;
        setTasksLoading(true);
        dispatch(
            getProject(project._id, (data) => {
                if (cancelled) return;
                setTasksLoading(false);
                const tasks = Array.isArray(data?.tasks) ? data.tasks : [];
                setTaskOptions(tasks);
            }),
        );
        return () => {
            cancelled = true;
        };
    }, [project?._id, dispatch]);

    const handleClientChange = (_, v) => {
        setClient(v);
        setAccount(null);
        setProject(null);
        setTask(null);
    };

    const handleAccountChange = (_, v) => {
        setAccount(v);
        setProject(null);
        setTask(null);
    };

    const handleProjectChange = (_, v) => {
        setProject(v);
        setTask(null);
    };

    const handleTaskChange = (_, v) => {
        setTask(v);
    };

    const canSubmit = useMemo(() => {
        if (!client || !account || !project) return false;
        if (!summary || !summary.trim()) return false;
        if (!date) return false;
        const hours = Number(duration);
        if (!Number.isFinite(hours) || hours <= 0 || hours > 24) return false;
        return !submitting;
    }, [client, account, project, summary, date, duration, submitting]);

    const handleClose = () => {
        if (!submitting && onClose) onClose(null);
    };

    const handleSubmit = () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setInlineError(null);
        const parentType = task ? "task" : "project";
        const parentId = task ? task._id : project._id;
        const payload = {
            parentType,
            parentId,
            client: client._id,
            account: account._id,
            project: project._id,
            task: task?._id || null,
            time: Math.round(parseFloat(duration) * 60),
            summary: summary.trim(),
            date,
            billable: !!billable,
        };
        dispatch(
            recordMeetingTime(
                meeting._id,
                payload,
                (data) => {
                    setSubmitting(false);
                    dispatch(showAlert({ severity: "success", message: "Time recorded" }));
                    if (onClose) onClose(data);
                },
                (error) => {
                    setSubmitting(false);
                    const msg = error?.response?.data?.message || error?.message || "Could not record time";
                    setInlineError(msg);
                },
            ),
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Record Meeting Time</DialogTitle>
            <StyledContent>
                {inlineError && (
                    <ErrorBox>
                        <Typography variant="body2" color="error">
                            {inlineError}
                        </Typography>
                    </ErrorBox>
                )}

                <Autocomplete
                    options={clientOptions}
                    loading={clientsLoading}
                    value={client}
                    onChange={handleClientChange}
                    getOptionLabel={(o) => o?.name || ""}
                    isOptionEqualToValue={(o, v) => o?._id === v?._id}
                    filterOptions={fuzzyFilter((o) => o?.name || "")}
                    renderInput={(params) => <TextField {...params} label="Client" required />}
                    disabled={submitting}
                />

                <Autocomplete
                    options={accountOptions}
                    loading={accountsLoading}
                    value={account}
                    onChange={handleAccountChange}
                    getOptionLabel={(o) => o?.name || ""}
                    isOptionEqualToValue={(o, v) => o?._id === v?._id}
                    filterOptions={fuzzyFilter((o) => o?.name || "")}
                    renderInput={(params) => <TextField {...params} label="Account" required />}
                    disabled={submitting || !client}
                />

                <Autocomplete
                    options={projectOptions}
                    loading={projectsLoading}
                    value={project}
                    onChange={handleProjectChange}
                    getOptionLabel={(o) => o?.name || ""}
                    isOptionEqualToValue={(o, v) => o?._id === v?._id}
                    filterOptions={fuzzyFilter((o) => o?.name || "")}
                    renderInput={(params) => <TextField {...params} label="Project" required />}
                    disabled={submitting || !account}
                />

                <Autocomplete
                    options={taskOptions}
                    loading={tasksLoading}
                    value={task}
                    onChange={handleTaskChange}
                    getOptionLabel={(o) => o?.name || o?.title || ""}
                    isOptionEqualToValue={(o, v) => o?._id === v?._id}
                    filterOptions={fuzzyFilter((o) => o?.name || o?.title || "")}
                    renderInput={(params) => <TextField {...params} label="Task (optional)" placeholder="Pick a task or leave blank" />}
                    disabled={submitting || !project}
                />

                <TextField label="Summary" required fullWidth value={summary} onChange={(e) => setSummary(e.target.value)} disabled={submitting} />

                <Row>
                    <TextField
                        label="Duration (hours)"
                        type="number"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        inputProps={{ min: 0.01, max: 24, step: 0.25 }}
                        required
                        disabled={submitting}
                    />
                    <AppDatePicker
                        label="Date"
                        value={date}
                        onChange={(v) => setDate(v)}
                        required
                        disabled={submitting}
                    />
                </Row>

                <FormControlLabel
                    control={<Switch checked={billable} onChange={(e) => setBillable(e.target.checked)} disabled={submitting} />}
                    label="Billable"
                />
            </StyledContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
                    Record Time
                    {submitting && <SubmitSpinner size={16} />}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MeetingTimeEntryDialog;
